import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Users, UserCheck, UserX, Shield, Building2, Ban, Download, ChevronDown } from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Input from '../../components/Input';
import Select from '../../components/Select';
import Badge from '../../components/Badge';
import { getUsers, exportUserEmails } from '../../utils/api';
import { formatDate, getAccountTypeBadgeVariant, getAccountTypeLabel } from '../../utils/helpers';

// Debounce hook for search input
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
}

export default function UserList() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Get initial state from URL params
    const urlFilter = searchParams.get('filter') || 'all';
    const urlSearch = searchParams.get('search') || '';

    // Local state for immediate UI updates
    const [filter, setFilter] = useState(urlFilter);
    const [searchInput, setSearchInput] = useState(urlSearch);
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    // Debounced search for URL and API
    const debouncedSearch = useDebounce(searchInput, 300);

    // Sync URL params when filter or debounced search changes
    useEffect(() => {
        const newParams = new URLSearchParams();

        if (filter && filter !== 'all') {
            newParams.set('filter', filter);
        }
        if (debouncedSearch) {
            newParams.set('search', debouncedSearch);
        }

        // Only update URL if params actually changed
        const currentParamsStr = searchParams.toString();
        const newParamsStr = newParams.toString();

        if (currentParamsStr !== newParamsStr) {
            setSearchParams(newParams, { replace: true });
        }
    }, [filter, debouncedSearch, searchParams, setSearchParams]);

    // Sync local state when URL changes (e.g., on back navigation)
    useEffect(() => {
        const newFilter = searchParams.get('filter') || 'all';
        const newSearch = searchParams.get('search') || '';

        if (newFilter !== filter) {
            setFilter(newFilter);
        }
        if (newSearch !== searchInput) {
            setSearchInput(newSearch);
        }
    }, [searchParams]); // Only depend on searchParams, not local state

    // Handle filter change
    const handleFilterChange = useCallback((newFilter) => {
        setFilter(newFilter);
    }, []);

    // Handle search change
    const handleSearchChange = useCallback((e) => {
        setSearchInput(e.target.value);
    }, []);

    // Handle email export
    const handleExportEmails = useCallback(async (exportFilter) => {
        setIsExporting(true);
        setExportMenuOpen(false);
        try {
            const blob = await exportUserEmails(exportFilter);

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const filterLabel = exportFilter || 'all';
            a.download = `user_emails_${filterLabel}_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Failed to export emails:', error);
            alert('Failed to export emails. Please try again.');
        } finally {
            setIsExporting(false);
        }
    }, []);

    // Query uses debounced search for actual API calls
    const { data, isLoading } = useQuery({
        queryKey: ['users', debouncedSearch, filter],
        queryFn: () => getUsers({ search: debouncedSearch, filter, limit: 50 }),
    });

    const counts = data?.data?.counts || {};

    const columns = [
        {
            header: 'User',
            accessor: 'username',
            render: (user) => (
                <div className="flex items-center gap-3">
                    {user.profilePicture ? (
                        <img
                            src={user.profilePicture}
                            alt={user.username}
                            className="w-10 h-10 rounded-full object-cover"
                        />
                    ) : (
                        <div className="w-10 h-10 rounded-full bg-accent-light dark:bg-accent-dark flex items-center justify-center">
                            <span className="font-medium">
                                {user.username?.charAt(0)?.toUpperCase()}
                            </span>
                        </div>
                    )}
                    <div>
                        <p className="font-medium">{user.username}</p>
                        <p className="text-sm text-muted-light dark:text-muted-dark">
                            {user.displayName}
                        </p>
                    </div>
                </div>
            ),
        },
        {
            header: 'Email',
            accessor: 'email',
            render: (user) => (
                <span className="text-sm">{user.email || 'N/A'}</span>
            ),
        },
        {
            header: 'Account Types',
            accessor: 'accountTypes',
            render: (user) => (
                <div className="flex flex-wrap gap-1">
                    {user.accountTypes && user.accountTypes.length > 0 ? (
                        user.accountTypes.map((type) => (
                            <Badge
                                key={type}
                                variant={getAccountTypeBadgeVariant(type)}
                            >
                                {getAccountTypeLabel(type)}
                            </Badge>
                        ))
                    ) : (
                        <span className="text-sm text-muted-light dark:text-muted-dark">
                            Regular
                        </span>
                    )}
                </div>
            ),
        },
        {
            header: 'Onboarding',
            accessor: 'onboardingCompleted',
            render: (user) => (
                <Badge variant={user.onboardingCompleted ? 'success' : 'warning'}>
                    {user.onboardingCompleted ? 'Complete' : 'Pending'}
                </Badge>
            ),
        },
        {
            header: 'Status',
            accessor: 'isBanned',
            render: (user) => (
                <Badge variant={user.isBanned ? 'danger' : 'success'}>
                    {user.isBanned ? 'Banned' : 'Active'}
                </Badge>
            ),
        },
        {
            header: 'Joined',
            accessor: 'createdAt',
            render: (user) => (
                <span className="text-sm">{formatDate(user.createdAt)}</span>
            ),
        },
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold mb-2">User Management</h1>
                    <p className="text-muted-light dark:text-muted-dark">
                        Manage user accounts and permissions
                    </p>
                </div>

                {/* Export Button */}
                <div className="relative">
                    <button
                        onClick={() => setExportMenuOpen(!exportMenuOpen)}
                        disabled={isExporting}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                        <Download size={18} />
                        {isExporting ? 'Exporting...' : 'Export Emails'}
                        <ChevronDown size={16} />
                    </button>

                    {exportMenuOpen && (
                        <>
                            <div
                                className="fixed inset-0 z-10"
                                onClick={() => setExportMenuOpen(false)}
                            />
                            <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                                <div className="py-1">
                                    <button
                                        onClick={() => handleExportEmails('all')}
                                        className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                                    >
                                        <Users size={16} className="text-blue-500" />
                                        All Users ({counts.total || 0})
                                    </button>
                                    <button
                                        onClick={() => handleExportEmails('onboarding_completed')}
                                        className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                                    >
                                        <UserCheck size={16} className="text-green-500" />
                                        Onboarded ({counts.onboardingCompleted || 0})
                                    </button>
                                    <button
                                        onClick={() => handleExportEmails('onboarding_pending')}
                                        className="w-full text-left px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                                    >
                                        <UserX size={16} className="text-yellow-500" />
                                        Pending ({counts.onboardingPending || 0})
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <Card
                    className={`cursor-pointer transition-all ${filter === 'all' ? 'ring-2 ring-primary' : 'hover:bg-accent-light/50 dark:hover:bg-accent-dark/50'}`}
                    onClick={() => handleFilterChange('all')}
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-blue-500/10">
                            <Users size={18} className="text-blue-500" />
                        </div>
                        <div>
                            <p className="text-xs text-muted-light dark:text-muted-dark">Total</p>
                            <p className="text-xl font-bold">{counts.total || 0}</p>
                        </div>
                    </div>
                </Card>
                <Card
                    className={`cursor-pointer transition-all ${filter === 'onboarding_completed' ? 'ring-2 ring-primary' : 'hover:bg-accent-light/50 dark:hover:bg-accent-dark/50'}`}
                    onClick={() => handleFilterChange('onboarding_completed')}
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-green-500/10">
                            <UserCheck size={18} className="text-green-500" />
                        </div>
                        <div>
                            <p className="text-xs text-muted-light dark:text-muted-dark">Onboarded</p>
                            <p className="text-xl font-bold">{counts.onboardingCompleted || 0}</p>
                        </div>
                    </div>
                </Card>
                <Card
                    className={`cursor-pointer transition-all ${filter === 'onboarding_pending' ? 'ring-2 ring-primary' : 'hover:bg-accent-light/50 dark:hover:bg-accent-dark/50'}`}
                    onClick={() => handleFilterChange('onboarding_pending')}
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-yellow-500/10">
                            <UserX size={18} className="text-yellow-500" />
                        </div>
                        <div>
                            <p className="text-xs text-muted-light dark:text-muted-dark">Pending</p>
                            <p className="text-xl font-bold">{counts.onboardingPending || 0}</p>
                        </div>
                    </div>
                </Card>
                <Card
                    className={`cursor-pointer transition-all ${filter === 'verified' ? 'ring-2 ring-primary' : 'hover:bg-accent-light/50 dark:hover:bg-accent-dark/50'}`}
                    onClick={() => handleFilterChange('verified')}
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-purple-500/10">
                            <Shield size={18} className="text-purple-500" />
                        </div>
                        <div>
                            <p className="text-xs text-muted-light dark:text-muted-dark">Verified</p>
                            <p className="text-xl font-bold">{counts.verified || 0}</p>
                        </div>
                    </div>
                </Card>
                <Card
                    className={`cursor-pointer transition-all ${filter === 'community' ? 'ring-2 ring-primary' : 'hover:bg-accent-light/50 dark:hover:bg-accent-dark/50'}`}
                    onClick={() => handleFilterChange('community')}
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-indigo-500/10">
                            <Building2 size={18} className="text-indigo-500" />
                        </div>
                        <div>
                            <p className="text-xs text-muted-light dark:text-muted-dark">Communities</p>
                            <p className="text-xl font-bold">{counts.community || 0}</p>
                        </div>
                    </div>
                </Card>
                <Card
                    className={`cursor-pointer transition-all ${filter === 'banned' ? 'ring-2 ring-primary' : 'hover:bg-accent-light/50 dark:hover:bg-accent-dark/50'}`}
                    onClick={() => handleFilterChange('banned')}
                >
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-red-500/10">
                            <Ban size={18} className="text-red-500" />
                        </div>
                        <div>
                            <p className="text-xs text-muted-light dark:text-muted-dark">Banned</p>
                            <p className="text-xl font-bold">{counts.banned || 0}</p>
                        </div>
                    </div>
                </Card>
            </div>

            <Card>
                {/* Filters */}
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                    <div className="flex-1">
                        <Input
                            placeholder="Search users..."
                            value={searchInput}
                            onChange={handleSearchChange}
                            className="w-full"
                        />
                    </div>
                    <Select
                        value={filter}
                        onChange={(e) => handleFilterChange(e.target.value)}
                        className="w-full md:w-56"
                    >
                        <option value="all">All Users ({counts.total || 0})</option>
                        <option value="onboarding_completed">✅ Onboarded ({counts.onboardingCompleted || 0})</option>
                        <option value="onboarding_pending">⏳ Pending Onboarding ({counts.onboardingPending || 0})</option>
                        <option value="verified">Verified ({counts.verified || 0})</option>
                        <option value="community">Communities ({counts.community || 0})</option>
                        <option value="banned">Banned ({counts.banned || 0})</option>
                    </Select>
                </div>

                {/* Table */}
                <Table
                    columns={columns}
                    data={data?.data?.users || []}
                    loading={isLoading}
                    onRowClick={(user) => navigate(`/users/${user._id}`)}
                    emptyState={{
                        title: 'No users found',
                        description: 'Try adjusting your search or filters',
                    }}
                />
            </Card>
        </div>
    );
}
